import React, { useState, useEffect } from 'react';
import { Head, useForm, Link, router } from '@inertiajs/react';
import DashboardLayout from '../../../components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Calendar, Package, Percent, Plus, Trash2, AlertCircle } from 'lucide-react';

interface Product {
  id: number;
  name: string;
  price: number;
  category: {
    name: string;
  };
}

interface PromotionType {
  value: string;
  label: string;
}

interface SelectedProduct {
  id: number;
  name: string;
  price: number;
  discount_percentage: number;
  category: {
    name: string;
  };
}

interface NewProps {
  products: Product[];
  promotion_types: PromotionType[];
  color_options: PromotionType[];
  store_logo?: string;
  store_name?: string;
  errors?: string[];
}

const New = ({ products, promotion_types, color_options, store_logo, store_name, errors }: NewProps) => {
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [color, setColor] = useState('red');

  const { data, setData, processing } = useForm({
    name: '',
    description: '',
    promotion_type: 'general',
    color: 'red',
    banner_text: '',
    start_date: '',
    end_date: '',
    active: true,
    countdown_enabled: false,
    countdown_end: '',
    default_discount_percentage: 10,
    image: null as File | null,
  });

  // Update data.color when color changes
  useEffect(() => {
    setData('color', color);
  }, [color]);

  const filteredProducts = products.filter(product => 
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    !selectedProducts.some(sp => sp.id === product.id)
  );

  const addProduct = (product: Product) => {
    const newProduct: SelectedProduct = {
      ...product,
      discount_percentage: data.default_discount_percentage || 10
    };
    setSelectedProducts([...selectedProducts, newProduct]);
  };

  const removeProduct = (productId: number) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };

  const updateProductDiscount = (productId: number, discount: number) => {
    setSelectedProducts(selectedProducts.map(p => 
      p.id === productId ? { ...p, discount_percentage: discount } : p
    ));
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('=== Frontend Submit Debug ===');
    console.log('Selected Products:', selectedProducts);

    // Si hay imagen, usar FormData
    if (data.image) {
      console.log('Using FormData (with image)');
      const formData = new FormData();
      
      // Agregar datos de promoción
      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && value !== undefined && key !== 'image') {
          formData.append(`promotion[${key}]`, String(value));
        }
      });
      
      // Agregar imagen
      formData.append('promotion[image]', data.image);
      
      // Agregar productos seleccionados
      selectedProducts.forEach((product, index) => {
        formData.append(`selected_products[${index}][id]`, String(product.id));
        formData.append(`selected_products[${index}][discount_percentage]`, String(product.discount_percentage));
      });

      // Debug FormData
      console.log('FormData entries:');
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }

      router.post('/admin/promotions', formData, {
        forceFormData: true,
      });
    } else {
      // Sin imagen, usar la estructura correcta para Rails
      console.log('Using structured data (no image)');
      
      const structuredData = {
        promotion: {
          name: data.name,
          description: data.description,
          promotion_type: data.promotion_type,
          color: data.color,
          banner_text: data.banner_text,
          start_date: data.start_date,
          end_date: data.end_date,
          active: data.active,
          countdown_enabled: data.countdown_enabled,
          countdown_end: data.countdown_end,
          default_discount_percentage: data.default_discount_percentage,
        },
        selected_products: selectedProducts.map(product => ({
          id: product.id,
          discount_percentage: product.discount_percentage
        }))
      };

      console.log('Structured data to send:', JSON.stringify(structuredData, null, 2));

      // Usar router.post en lugar de useForm.post para mejor control
      router.post('/admin/promotions', structuredData, {
        preserveScroll: true,
        onSuccess: () => {
          console.log('Creation successful');
        },
        onError: (errors) => {
          console.log('Creation errors:', errors);
        }
      });
    }
  };

  const calculateDiscountedPrice = (price: number, discount: number) => {
    return price - (price * (discount / 100));
  };

  return (
    <DashboardLayout storeLogo={store_logo} storeName={store_name}>
      <Head title="Nueva Promoción" />
      
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Nueva Promoción</h1>
          <p className="text-muted-foreground">
            Crea una nueva promoción con descuentos para múltiples productos
          </p>
        </div>

        {/* Error Messages */}
        {errors && errors.length > 0 && (
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Errores en el formulario
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-1">
                {errors.map((error, index) => (
                  <li key={index} className="text-sm text-destructive">{error}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        <form onSubmit={submit} className="space-y-8">
          {/* Información Básica */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Información de la Promoción
              </CardTitle>
              <CardDescription>
                Configura los detalles básicos de tu promoción
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre de la Promoción *</Label>
                  <Input
                    id="name"
                    placeholder="ej. Oferta de Navidad 2024"
                    value={data.name}
                    onChange={(e) => setData('name', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="promotion_type">Tipo de Promoción</Label>
                  <Select onValueChange={(value) => setData('promotion_type', value)} value={data.promotion_type}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {promotion_types.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descripción</Label>
                <Textarea
                  id="description"
                  placeholder="Describe los detalles de tu promoción..."
                  value={data.description}
                  onChange={(e) => setData('description', e.target.value)}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="banner_text">Texto del Banner</Label>
                <Input
                  id="banner_text"
                  placeholder="¡Aprovecha nuestros descuentos!"
                  value={data.banner_text}
                  onChange={(e) => setData('banner_text', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="image">Imagen de la Promoción</Label>
                <Input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setData('image', e.target.files?.[0] || null)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Configuración de Fechas */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Fechas y Estado
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Fecha de Inicio *</Label>
                  <Input
                    id="start_date"
                    type="datetime-local"
                    value={data.start_date}
                    onChange={(e) => setData('start_date', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="end_date">Fecha de Fin *</Label>
                  <Input
                    id="end_date"
                    type="datetime-local"
                    value={data.end_date}
                    onChange={(e) => setData('end_date', e.target.value)}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="active">Promoción Activa</Label>
                  <p className="text-sm text-muted-foreground">
                    La promoción estará visible en la tienda
                  </p>
                </div>
                <Switch
                  id="active"
                  checked={data.active}
                  onCheckedChange={(checked) => setData('active', checked)}
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="countdown_enabled">Contador Regresivo</Label>
                    <p className="text-sm text-muted-foreground">
                      Mostrar un contador regresivo en la promoción
                    </p>
                  </div>
                  <Switch
                    id="countdown_enabled"
                    checked={data.countdown_enabled}
                    onCheckedChange={(checked) => setData('countdown_enabled', checked)}
                  />
                </div>

                {data.countdown_enabled && (
                  <div className="space-y-2">
                    <Label htmlFor="countdown_end">Fin del Contador</Label>
                    <Input
                      id="countdown_end"
                      type="datetime-local"
                      value={data.countdown_end}
                      onChange={(e) => setData('countdown_end', e.target.value)}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Selección de Productos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="h-5 w-5" />
                Productos y Descuentos
              </CardTitle>
              <CardDescription>
                Selecciona los productos que participarán en esta promoción
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="default_discount">Descuento por Defecto (%)</Label>
                  <Input
                    id="default_discount"
                    type="number"
                    min="1"
                    max="100"
                    value={data.default_discount_percentage}
                    onChange={(e) => setData('default_discount_percentage', Number(e.target.value))}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="color">Color Principal</Label>
                  <select
                    id="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {color_options.map((colorOption) => (
                      <option key={colorOption.value} value={colorOption.value}>
                        {colorOption.label}
                      </option>
                    ))}
                  </select>
                </div>

               <div className="space-y-2">
                  <Label htmlFor="search">Buscar Productos</Label>
                  <Input
                    id="search"
                    placeholder="Buscar por nombre..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Lista de productos disponibles */}
              {searchTerm && (
                <div className="border rounded-lg p-4 max-h-48 overflow-y-auto">
                  <div className="text-sm font-medium mb-2">Productos disponibles:</div>
                  <div className="space-y-2">
                    {filteredProducts.slice(0, 5).map((product) => (
                      <div key={product.id} className="flex items-center justify-between p-2 hover:bg-muted rounded">
                        <div>
                          <div className="font-medium">{product.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {product.category.name} • S/. {product.price}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addProduct(product)}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Productos seleccionados */}
              {selectedProducts.length > 0 && (
                <div>
                  <div className="text-sm font-medium mb-4">
                    Productos seleccionados ({selectedProducts.length}):
                  </div>
                  <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Producto</TableHead>
                          <TableHead>Precio Original</TableHead>
                          <TableHead>Descuento (%)</TableHead>
                          <TableHead>Precio Final</TableHead>
                          <TableHead className="w-[50px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedProducts.map((product) => (
                          <TableRow key={product.id}>
                            <TableCell>
                              <div>
                                <div className="font-medium">{product.name}</div>
                                <Badge variant="outline" className="text-xs">
                                  {product.category.name}
                                </Badge>
                              </div>
                            </TableCell>
                            <TableCell>S/. {product.price}</TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="1"
                                max="100"
                                value={product.discount_percentage}
                                onChange={(e) => updateProductDiscount(product.id, Number(e.target.value))}
                                className="w-20"
                              />
                            </TableCell>
                            <TableCell className="font-medium text-green-600">
                              S/. {calculateDiscountedPrice(product.price, product.discount_percentage).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeProduct(product.id)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {selectedProducts.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Package className="h-8 w-8 mx-auto mb-2" />
                  <p>No hay productos seleccionados</p>
                  <p className="text-sm">Busca y selecciona productos para incluir en la promoción</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Botones de Acción */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link href="/admin/promotions">Cancelar</Link>
            </Button>
            <Button 
              type="submit" 
              disabled={processing || selectedProducts.length === 0}
              className="gap-2"
            >
              {processing ? "Creando..." : "Crear Promoción"}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default New;
